import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search,
  Sparkles,
  Wrench,
  Activity,
  Ruler,
  Layers,
  ChevronRight,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Calculator,
  X,
  Check,
  StickyNote,
  Clock,
  Bot,
  Send
} from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES } from '@/config/images';


const categoryIcons = {
  surface_quality: Sparkles,
  tool_issues: Wrench,
  motion_stability: Activity,
  accuracy: Ruler,
  material_specific: Layers
};

const categoryColors = {
  surface_quality: { bg: 'from-purple-500/50 to-purple-600/30', border: 'border-purple-500/50', text: 'text-purple-400' },
  tool_issues: { bg: 'from-red-500/50 to-red-600/30', border: 'border-red-500/50', text: 'text-red-400' },
  motion_stability: { bg: 'from-blue-500/50 to-blue-600/30', border: 'border-blue-500/50', text: 'text-blue-400' },
  accuracy: { bg: 'from-green-500/50 to-green-600/30', border: 'border-green-500/50', text: 'text-green-400' },
  material_specific: { bg: 'from-amber-500/50 to-amber-600/30', border: 'border-amber-500/50', text: 'text-amber-400' }
};

const Troubleshooting = () => {
  const { user } = useContext(AuthContext);
  const [issues, setIssues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userAttempts, setUserAttempts] = useState([]);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [noteInputs, setNoteInputs] = useState({});
  const [savingAttempt, setSavingAttempt] = useState(null);

  // AI assistant state
  const [machines, setMachines] = useState([]);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSessionId, setAiSessionId] = useState(null);
  const aiEndRef = useRef(null);

  const token = localStorage.getItem('token');
  const activeMachine = machines[0] || null;

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedIssue && token) {
      fetchUserAttempts(selectedIssue.id);
    }
  }, [selectedIssue, token]);

  // Fetch user's machines for AI context
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/machines`).then(r => setMachines(r.data || [])).catch(() => {});
  }, [token]);

  // Reset AI chat when issue changes
  useEffect(() => {
    if (!selectedIssue) {
      setAiMessages([]);
      setAiSessionId(null);
      return;
    }
    const machine = machines[0];
    const machineLabel = machine
      ? (machine.name || [machine.brand, machine.model].filter(Boolean).join(' ') || 'your machine')
      : null;
    const intro = machineLabel
      ? `I'm ready to help troubleshoot "${selectedIssue.issue_name}" on ${machineLabel}. Describe what you're observing and I'll give you targeted advice.`
      : `I'm ready to help troubleshoot "${selectedIssue.issue_name}". Describe what you're observing and I'll give you targeted advice.`;
    setAiMessages([{ role: 'assistant', content: intro }]);
    setAiSessionId(null);
    setAiInput('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIssue]);

  const fetchData = async () => {
    try {
      const [issuesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/troubleshooting/wizard/issues`),
        axios.get(`${API}/troubleshooting/wizard/categories`)
      ]);
      setIssues(issuesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch troubleshooting data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserAttempts = async (issueId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/troubleshooting/wizard/attempts?issue_id=${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAttempts(response.data);
    } catch (error) {
      console.error('Failed to fetch user attempts:', error);
    }
  };

  const handleMarkTried = async (action) => {
    if (!token || !selectedIssue) return;
    setSavingAttempt(action);
    try {
      await axios.post(`${API}/troubleshooting/wizard/attempts`, {
        issue_id: selectedIssue.id,
        action_tried: action,
        notes: noteInputs[action] || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUserAttempts(selectedIssue.id);
      setNoteInputs(prev => ({ ...prev, [action]: '' }));
      setExpandedNotes(prev => ({ ...prev, [action]: false }));
    } catch (error) {
      console.error('Failed to save attempt:', error);
    } finally {
      setSavingAttempt(null);
    }
  };

  const handleDeleteAttempt = async (attemptId) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/troubleshooting/wizard/attempts/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUserAttempts(selectedIssue.id);
    } catch (error) {
      console.error('Failed to delete attempt:', error);
    }
  };

  const isActionTried = (action) => {
    return userAttempts.some(a => a.action_tried === action);
  };

  const getAttemptForAction = (action) => {
    return userAttempts.find(a => a.action_tried === action);
  };

  const handleAiSend = async (e) => {
    e.preventDefault();
    if (!aiInput.trim() || aiLoading) return;

    const isFirstUserMessage = !aiMessages.some(m => m.role === 'user');
    const messageToSend = isFirstUserMessage
      ? `Troubleshooting issue: "${selectedIssue.issue_name}"\nKnown causes: ${selectedIssue.causes.slice(0, 3).join('; ')}\n\nMy question: ${aiInput}`
      : aiInput;

    setAiMessages(prev => [...prev, { role: 'user', content: aiInput }]);
    setAiInput('');
    setAiLoading(true);

    try {
      const res = await axios.post(`${API}/ai-assistant/chat`, {
        message: messageToSend,
        session_id: aiSessionId || undefined,
        machine_id: activeMachine?.id || undefined,
      });
      setAiMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setAiSessionId(res.data.session_id);
      setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', content: 'Failed to get a response. Please try again.' }]);
    } finally {
      setAiLoading(false);
    }
  };

  const filteredIssues = useMemo(() => {
    let filtered = issues;
    
    if (selectedCategory) {
      filtered = filtered.filter(issue => issue.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(issue => 
        issue.issue_name.toLowerCase().includes(query) ||
        issue.explanation.toLowerCase().includes(query) ||
        issue.causes.some(c => c.toLowerCase().includes(query)) ||
        issue.actions.some(a => a.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [issues, selectedCategory, searchQuery]);

  const groupedIssues = useMemo(() => {
    const grouped = {};
    filteredIssues.forEach(issue => {
      if (!grouped[issue.category]) {
        grouped[issue.category] = [];
      }
      grouped[issue.category].push(issue);
    });
    return grouped;
  }, [filteredIssues]);

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : categoryId;
  };

  if (isLoading) {
    return (
      <div data-testid="troubleshooting-loading" className="max-w-6xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-[#78716C] animate-pulse">Loading troubleshooting wizard...</div>
      </div>
    );
  }

  // Detail view for selected issue
  if (selectedIssue) {
    const colors = categoryColors[selectedIssue.category] || categoryColors.surface_quality;
    const Icon = categoryIcons[selectedIssue.category] || Sparkles;
    
    return (
      <div data-testid="troubleshooting-detail" className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => {
            setSelectedIssue(null);
            setUserAttempts([]);
            setExpandedNotes({});
            setNoteInputs({});
          }}
          className="text-[#78716C] hover:text-[#292524] mb-2"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to issues
        </Button>

        {/* Issue Header */}
        <Card className={`bg-gradient-to-br ${colors.bg} ${colors.border} border-2`}>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl bg-black/60 ${colors.text}`}>
                <Icon className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <Badge variant="outline" className={`${colors.text} ${colors.border} mb-2`}>
                  {getCategoryName(selectedIssue.category)}
                </Badge>
                <CardTitle className="text-2xl text-[#292524] mb-2">{selectedIssue.issue_name}</CardTitle>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* What's Likely Happening */}
        <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#292524] flex items-center gap-2 text-lg">
              <Search className="w-5 h-5 text-blue-400" />
              What&apos;s likely happening
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#57534E] leading-relaxed">{selectedIssue.explanation}</p>
          </CardContent>
        </Card>

        {/* Common Causes */}
        <Card className="bg-gradient-to-br from-amber-900/20 to-white/60 border-amber-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#292524] flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Common causes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {selectedIssue.causes.map((cause, index) => (
                <li key={index} className="text-[#57534E] flex items-start gap-3">
                  <span className="text-amber-400 font-bold mt-0.5">•</span>
                  <span>{cause}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* What to Try First */}
        <Card className="bg-gradient-to-br from-emerald-900/20 to-white/60 border-emerald-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#292524] flex items-center gap-2 text-lg">
              <Wrench className="w-5 h-5 text-emerald-400" />
              What to try first (safe fixes)
            </CardTitle>
            <CardDescription className="text-[#78716C] text-sm">
              These are starting recommendations. Always test conservatively on your machine.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4">
              {selectedIssue.actions.map((action, index) => {
                const tried = isActionTried(action);
                const attempt = getAttemptForAction(action);
                const isExpanded = expandedNotes[action];
                
                return (
                  <li key={index} className="text-[#57534E]">
                    <div className={`flex items-start gap-3 p-3 rounded-lg transition-all ${tried ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-black/20'}`}>
                      <span className={`font-mono font-bold mt-0.5 ${tried ? 'text-emerald-400' : 'text-emerald-400'}`}>
                        {index + 1}.
                      </span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={tried ? 'line-through opacity-60' : ''}>{action}</span>
                          {token && (
                            <div className="flex items-center gap-2 shrink-0">
                              {tried ? (
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-emerald-400 border-emerald-500/50 text-xs">
                                    <Check className="w-3 h-3 mr-1" />
                                    Tried
                                  </Badge>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-6 w-6 p-0 text-[#78716C] hover:text-red-400"
                                    onClick={() => handleDeleteAttempt(attempt.id)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs text-[#78716C] hover:text-[#292524]"
                                    onClick={() => setExpandedNotes(prev => ({ ...prev, [action]: !prev[action] }))}
                                  >
                                    <StickyNote className="w-3 h-3 mr-1" />
                                    Note
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                                    onClick={() => handleMarkTried(action)}
                                    disabled={savingAttempt === action}
                                  >
                                    {savingAttempt === action ? (
                                      <span className="animate-pulse">Saving...</span>
                                    ) : (
                                      <>
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Mark as tried
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Notes input for non-tried actions */}
                        {isExpanded && !tried && (
                          <div className="mt-3 space-y-2">
                            <Textarea
                              placeholder="Add your notes about this solution..."
                              value={noteInputs[action] || ''}
                              onChange={(e) => setNoteInputs(prev => ({ ...prev, [action]: e.target.value }))}
                              className="bg-black/60 border-[#D6D3D1] text-[#292524] text-sm min-h-[60px]"
                            />
                          </div>
                        )}
                        
                        {/* Show saved notes */}
                        {attempt?.notes && (
                          <div className="mt-2 p-2 bg-black/60 rounded border border-[#D6D3D1]">
                            <div className="flex items-center gap-1 text-xs text-[#78716C] mb-1">
                              <Clock className="w-3 h-3" />
                              {new Date(attempt.tried_at).toLocaleDateString()}
                            </div>
                            <p className="text-sm text-[#78716C]">{attempt.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>

        {/* CNC-Helpdesk Recommendation */}
        <Card className="bg-gradient-to-br from-[#F59E0B]/20 to-white/60 border-[#F59E0B]/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-[#292524] flex items-center gap-2 text-lg">
              <Lightbulb className="w-5 h-5 text-[#B45309]" />
              CNC-Helpdesk recommendation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#57534E] leading-relaxed">{selectedIssue.recommendation}</p>
            <Button 
              variant="outline" 
              className="mt-4 border-[#F59E0B]/50 text-[#B45309] hover:bg-[#B45309]/20"
              onClick={() => window.location.href = '/calculator'}
            >
              <Calculator className="w-4 h-4 mr-2" />
              Open Calculator
            </Button>
          </CardContent>
        </Card>

        {/* Ask AI */}
        {token && (
          <Card className="bg-[#1a1a1a] border-[#3a3a3a]">
            <CardHeader className="pb-3">
              <CardTitle className="text-[#e67e22] flex items-center gap-2 text-lg">
                <Bot className="w-5 h-5" />
                Ask AI about this issue
              </CardTitle>
              {activeMachine && (
                <CardDescription className="text-[#78716C] text-sm">
                  Machine context: {activeMachine.name || [activeMachine.brand, activeMachine.model].filter(Boolean).join(' ') || 'Your machine'}
                  {activeMachine.spindle_brand ? ` · ${activeMachine.spindle_brand}` : ''}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <ScrollArea className="h-[300px] rounded-lg border border-[#3a3a3a] bg-[#141414] p-3">
                <div className="space-y-3">
                  {aiMessages.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      {m.role === 'assistant' && (
                        <Bot className="w-4 h-4 text-[#e67e22] flex-shrink-0 mt-0.5" />
                      )}
                      <div className={`text-sm leading-relaxed whitespace-pre-wrap rounded-lg px-3 py-2 max-w-[85%] ${
                        m.role === 'user'
                          ? 'bg-[#B45309]/20 border border-[#B45309]/30 text-[#e8e8e8]'
                          : 'text-[#e67e22]'
                      }`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {aiLoading && (
                    <div className="flex gap-2">
                      <Bot className="w-4 h-4 text-[#e67e22] flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-[#e67e22] animate-pulse">Thinking…</div>
                    </div>
                  )}
                  <div ref={aiEndRef} />
                </div>
              </ScrollArea>
              <form onSubmit={handleAiSend} className="flex gap-2">
                <Input
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  placeholder="Describe what you're seeing…"
                  className="bg-[#1f1f1f] border-[#3a3a3a] text-white placeholder:text-zinc-500 flex-1"
                  disabled={aiLoading}
                />
                <Button
                  type="submit"
                  disabled={aiLoading || !aiInput.trim()}
                  className="bg-[#e67e22] hover:bg-[#d35400] text-white"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <p className="text-center text-[#78716C] text-sm italic">
          These are starting recommendations. Always test conservatively on your machine.
        </p>
      </div>
    );
  }

  // Main issue list view
  return (
    <div data-testid="troubleshooting-page" className="space-y-8">
      {/* Hero Section */}
      <div 
        className="relative w-full h-48 md:h-56 rounded-lg overflow-hidden"
        style={{
          backgroundImage: `url('${IMAGES.ROUTER_TROUBLESHOOTING}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        <div className="relative z-10 h-full flex items-center px-6 md:px-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Diagnose</div>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Troubleshooting <span className="text-[#e67e22]">Wizard</span>
            </h1>
            <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
              Diagnose and solve common CNC problems based on what you observe.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">

      {/* Search and Filter Bar */}
      <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[#292524] text-lg">What issue are you experiencing?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#78716C]" />
            <Input
              data-testid="search-input"
              placeholder="Search issues, causes, or solutions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/60 border-[#D6D3D1] text-[#292524] placeholder:text-[#78716C]"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-[#78716C] hover:text-[#292524]"
                onClick={() => setSearchQuery('')}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className={selectedCategory === null 
                ? 'bg-[#B45309] hover:bg-[#B45309]/90 text-black' 
                : 'border-[#D6D3D1] text-[#78716C] hover:text-[#292524] hover:border-gray-400'
              }
              data-testid="filter-all"
            >
              All Issues
            </Button>
            {categories.map(cat => {
              const Icon = categoryIcons[cat.id] || Sparkles;
              const isSelected = selectedCategory === cat.id;
              const colors = categoryColors[cat.id] || categoryColors.surface_quality;
              
              return (
                <Button
                  key={cat.id}
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedCategory(isSelected ? null : cat.id)}
                  className={isSelected 
                    ? `bg-gradient-to-r ${colors.bg} ${colors.border} ${colors.text}` 
                    : 'border-[#D6D3D1] text-[#78716C] hover:text-[#292524] hover:border-gray-400'
                  }
                  data-testid={`filter-${cat.id}`}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {cat.name}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      {(searchQuery || selectedCategory) && (
        <div className="text-[#78716C] text-sm">
          Found {filteredIssues.length} issue{filteredIssues.length !== 1 ? 's' : ''}
          {selectedCategory && ` in ${getCategoryName(selectedCategory)}`}
          {searchQuery && ` matching "${searchQuery}"`}
        </div>
      )}

      {/* Issue List */}
      {filteredIssues.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-[#57534E] mx-auto mb-4" />
            <p className="text-[#78716C] text-lg">No issues found</p>
            <p className="text-[#78716C] text-sm mt-1">Try adjusting your search or filters</p>
            <Button
              variant="outline"
              className="mt-4 border-[#D6D3D1] text-[#78716C]"
              onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
            >
              Clear filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedIssues).map(([categoryId, categoryIssues]) => {
            const colors = categoryColors[categoryId] || categoryColors.surface_quality;
            const Icon = categoryIcons[categoryId] || Sparkles;
            
            return (
              <div key={categoryId} className="space-y-3">
                {/* Category Header (only show if not filtering by category) */}
                {!selectedCategory && (
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                    <h2 className={`text-lg font-semibold ${colors.text}`}>
                      {getCategoryName(categoryId)}
                    </h2>
                    <Badge variant="outline" className={`${colors.text} ${colors.border} text-xs`}>
                      {categoryIssues.length}
                    </Badge>
                  </div>
                )}
                
                {/* Issue Cards */}
                <div className="grid gap-3">
                  {categoryIssues.map(issue => (
                    <Card 
                      key={issue.id}
                      className={`bg-white/80 backdrop-blur-sm border-[#D6D3D1] hover:${colors.border} transition-all cursor-pointer group`}
                      onClick={() => setSelectedIssue(issue)}
                      data-testid={`issue-card-${issue.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg bg-black/60 ${colors.text} group-hover:scale-110 transition-transform`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-[#292524] font-medium group-hover:text-[#B45309] transition-colors">
                                {issue.issue_name}
                              </h3>
                              <p className="text-[#78716C] text-sm truncate mt-0.5">
                                {issue.explanation.substring(0, 80)}...
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#78716C] group-hover:text-[#B45309] group-hover:translate-x-1 transition-all shrink-0 ml-2" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default Troubleshooting;
